/* eslint-disable import/no-default-export, import/order, max-lines, max-lines-per-function, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { unref } from "common/vue/reactivity";
/* eslint-disable max-lines */

// eslint-disable-next-line import/prefer-default-export
import { capitalize } from "common/utils/format/word";
import { BUSINESS_LINE } from "common/utils/app-registry/contants";
import getVariantInfo from "common/jiffy/get-variant";
import { getPageName } from "common/utils/pages";
import sendServerSideConversion from "common/utils/conversion-tracking";
import logger from "../logger";
import retrySubmitRudderstackEvents from "./retry-rudderstack";
import api from "../api";
import trackRudderstackEvent from "./track-rudderstack-event";

export const track = (
  eventType: string,
  properties: Record<string, unknown>,
  sendToMixpanel = false
) => {
  if (window.rudderanalytics) {
    trackRudderstackEvent(eventType, properties, sendToMixpanel);
  } else {
    retrySubmitRudderstackEvents(eventType, properties, sendToMixpanel);
  }

  if (window.ANALYTICS_LOGS_ENABLED) {
    logger.log(`Rudderstack Track ${eventType}: ${JSON.stringify(properties)}`);
  }
};

const checkoutSteps = ["order_checkout", "review_your_order", "checkout_complete"];
const $getOrderNumberContainer = () =>
  document.querySelector<HTMLElement>(".js-order-number-container");
const $getCheckoutContainer = () => document.getElementById("checkout");
const $getActiveShipmentRadioButtons = () =>
  document.querySelectorAll(
    '.mcu-order-shipping-speed-selector__radio-wrapper.active input[type="radio"]'
  );
const $getCartSummarySubtotal = () =>
  document.querySelector<HTMLElement>(".js-cart-summary-subtotal");

type CheckoutInitiatedData = {
  other_platform_data: {
    tiktok: {
      event_id: string;
      event_value: string | undefined;
      currency: string | undefined;
    };
  };
};

const checkoutInitiatedAdditionalData = (): CheckoutInitiatedData => {
  const $orderNumberContainer = $getOrderNumberContainer();
  const $cartSummarySubtotal = $getCartSummarySubtotal();
  const orderNumber = $orderNumberContainer?.dataset?.["orderNumber"];
  const cartTotal = $cartSummarySubtotal?.dataset?.["cartTotal"];
  const currencyCode = $cartSummarySubtotal?.dataset?.["currencyCode"];

  // we exclude this for rudderstack, but can use it for other platforms
  return {
    other_platform_data: {
      tiktok: {
        event_id: `CI:${orderNumber}:${Date.now()}`,
        event_value: cartTotal,
        currency: currencyCode
      }
    }
  };
};

const $getCvcSource = () =>
  document.querySelector<HTMLElement>("#js-order-payment-method-rerequest-cvc");

type CheckoutStepName = "order_checkout" | "review_your_order" | "checkout_complete";

export const trackCheckoutStepViewed = (stepName: CheckoutStepName) => {
  const stepNumber = checkoutSteps.indexOf(stepName) + 1;
  const $checkoutContainer = $getCheckoutContainer();
  const $orderNumberContainer = $getOrderNumberContainer();
  let shippingMethods = [];

  if (stepNumber === 0) return;

  if ($checkoutContainer) {
    const { checkoutData } = $checkoutContainer.dataset;
    if (checkoutData) {
      shippingMethods = JSON.parse(checkoutData)?.shipping_methods;
    }
  } else {
    shippingMethods = Array.from($getActiveShipmentRadioButtons()).map(radio => unref(radio));
  }

  track("Checkout Step Viewed", {
    store_id: $orderNumberContainer?.dataset?.["storeId"],
    is_app: Boolean(window.MOBILE_APP),
    step_name: stepName,
    step_number: stepNumber,
    shipping_methods: shippingMethods,
    order_number: $orderNumberContainer?.dataset?.["orderNumber"],
    cart_name: $orderNumberContainer?.dataset?.["cartName"],
    cvc_requested: !!$getCvcSource()
  });
};

export const trackCheckoutStepCompleted = (stepName: CheckoutStepName, clickTarget = "") => {
  const stepNumber = checkoutSteps.indexOf(stepName) + 1;
  const $checkoutContainer = $getCheckoutContainer();
  const $orderNumberContainer = $getOrderNumberContainer();
  let shippingMethods = [];

  if (stepNumber === 0) return;

  if ($checkoutContainer) {
    const { checkoutData } = $checkoutContainer.dataset;
    if (checkoutData) {
      shippingMethods = JSON.parse(checkoutData)?.shipping_methods;
    }
  } else {
    shippingMethods = Array.from($getActiveShipmentRadioButtons()).map(radio => unref(radio));
  }

  const additionalData: CheckoutInitiatedData | Record<string, never> =
    stepName === "order_checkout" ? checkoutInitiatedAdditionalData() : {};

  track("Checkout Step Completed", {
    store_id: $orderNumberContainer?.dataset?.["storeId"],
    is_app: Boolean(window.MOBILE_APP),
    step_name: stepName,
    step_number: stepNumber,
    shipping_methods: shippingMethods,
    click_target: clickTarget,
    order_number: $orderNumberContainer?.dataset?.["orderNumber"],
    cart_name: $orderNumberContainer?.dataset?.["cartName"],
    delivery_date_changed: null,
    expected_delivery_date: null,
    cvc_requested: !!$getCvcSource(),
    ...additionalData
  });

  if (stepName === "order_checkout" && "other_platform_data" in additionalData) {
    sendServerSideConversion("InitiateCheckout", additionalData.other_platform_data.tiktok);
  }
};

export const trackProductViewed = async (eventData: Record<string, unknown>) => {
  const $orderNumberContainer = $getOrderNumberContainer();

  track("Product Viewed", {
    store_id: $orderNumberContainer?.dataset?.["storeId"],
    ef_tid: $orderNumberContainer?.dataset?.["efTid"],
    ...eventData
  });
};

type AdditionalPayload = {
  is_app: boolean;
  business_line: unknown;
  zoom_percentage: number;
  store_code?: string;
  catalog_number?: string;
  multi_artwork?: boolean;
};

type ImageTrackingPayload = Record<string, unknown> & {
  business_line?: unknown;
  artworks_count?: number;
};

const additionalPayload = (rawPayload?: ImageTrackingPayload): AdditionalPayload => {
  const currentStoreCode = document.documentElement.lang === "en-CA" ? "ca" : "us";
  const catalogNumber =
    document.querySelector<HTMLElement>(".js-dtf-product")?.dataset?.["catalogNumber"] ||
    document.querySelector<HTMLElement>(".js-quantity-form-input")?.dataset?.["catalogNumber"];

  return {
    is_app: Boolean(window.MOBILE_APP),
    business_line:
      rawPayload && rawPayload.business_line ? rawPayload.business_line : BUSINESS_LINE.DTF,
    zoom_percentage: Math.round((window.outerWidth / window.innerWidth) * 100),
    ...(currentStoreCode && { store_code: currentStoreCode }),
    ...(catalogNumber && { catalog_number: catalogNumber }),
    ...(rawPayload?.artworks_count !== undefined && {
      multi_artwork: rawPayload.artworks_count > 1
    })
  };
};

export const trackRudderstackImageUploadStarted = (rawPayload: ImageTrackingPayload) => {
  const payload = {
    ...rawPayload,
    ...additionalPayload(rawPayload)
  };

  track("Image Upload Started", payload);
};

type ImageUploadCompletedPayload = ImageTrackingPayload & {
  file?: { type: string; size: number; name: string };
  artwork?: { id?: string | number; file?: { type: string; size: number; name: string } };
  artwork_id?: string | number;
};

export const trackRudderstackImageUploadCompleted = (rawPayload: ImageUploadCompletedPayload) => {
  const { file, artwork, ...cleanPayload } = rawPayload;
  const uploadedFile = file || artwork?.file;
  const artworkId = rawPayload.artwork_id ?? artwork?.id;

  const payload = {
    ...cleanPayload,
    ...(artworkId != null && { artwork_id: artworkId }),
    ...(uploadedFile && {
      file_type: uploadedFile.type,
      file_size: uploadedFile.size,
      file_name: uploadedFile.name
    }),
    ...additionalPayload(rawPayload)
  };

  track("Image Upload Completed", payload);
};

export const trackRudderstackImageProcessingCompleted = (
  rawPayload: ImageTrackingPayload & { artwork_id?: string }
) => {
  const payload = {
    ...rawPayload,
    ...additionalPayload(rawPayload)
  };

  track("Image Processing Completed", payload);

  // Send artwork_id here only if user is logged in
  if (rawPayload.artwork_id) {
    // Check if user is logged in by looking for user_id meta tag
    const userIdMeta = document.querySelector<HTMLMetaElement>('meta[property="user_id"]');
    const userId = userIdMeta?.content;
    if (userId && userId !== "") {
      api.jiffyshirts.trackEngagement.processedArtwork({ artwork_id: rawPayload.artwork_id });
    }
  }
};

export const trackRudderstackImageUploadError = (rawPayload: ImageTrackingPayload) => {
  const payload = {
    ...rawPayload,
    ...additionalPayload(rawPayload)
  };

  track("Image Upload Error", payload);
};

export const trackRudderstackImageUploadRetry = (rawPayload: ImageTrackingPayload) => {
  const payload = {
    ...rawPayload,
    ...additionalPayload(rawPayload)
  };

  track("Image Upload Retry", payload);
};

export const trackRudderstackImageRemoved = (rawPayload: ImageTrackingPayload) => {
  const payload = {
    ...rawPayload,
    ...additionalPayload(rawPayload)
  };

  track("Image Removed", payload);
};

type ArtworkShape = {
  enhancementTier?: string;
  file?: { type: string; size: number; name: string };
  filetype?: string;
  filesize?: number;
  filename?: string;
  pricing?: { enhancementTierPrice?: string };
  removeBackground?: boolean;
  metadata?: { dpi?: number };
  price?: string | number;
};

type ImageEditedPayload = ImageTrackingPayload & {
  artwork: ArtworkShape | null;
  remove_background_toggle_switch?: string;
  price?: string | number;
  edit_type?: string;
};

export const trackRudderstackImageEdited = (rawPayload: ImageEditedPayload) => {
  const { artwork, ...cleanPayload } = rawPayload;
  const selection = artwork?.enhancementTier || "PrintPerfect";

  const payload: Record<string, unknown> = {
    ...cleanPayload,
    file_type: artwork?.file?.type || artwork?.filetype,
    file_size: artwork?.file?.size || artwork?.filesize,
    file_name: artwork?.file?.name || artwork?.filename,
    print_perfect_selection:
      selection === "full_enhancements" ? "PrintPerfect" : capitalize(selection),
    print_perfect_price: artwork?.pricing?.enhancementTierPrice || "0.0",
    remove_background_toggle: artwork?.removeBackground,
    remove_background_toggle_switch: rawPayload.remove_background_toggle_switch || "no_change",
    dpi: artwork?.metadata?.dpi,
    ...additionalPayload(rawPayload)
  };

  if (payload["edit_type"] === "Print as uploaded") {
    payload["print_perfect_selection"] = null;
    payload["print_perfect_price"] = "0.0";
    payload["remove_background_toggle"] = null;
    payload["remove_background_toggle_switch"] = null;
  } else if (payload["edit_type"] === "Jiffy Print Perfect") {
    if (payload["print_perfect_selection"] === "Custom") {
      payload["remove_background_toggle"] = null;
      payload["remove_background_toggle_switch"] = null;
    }
  }

  const payloadWithPrice = {
    old_price: payload["price"] || artwork?.price,
    new_price: payload["price"] || artwork?.price,
    ...payload
  };

  track("Image Edited", payloadWithPrice);
};

type BlanksProductPricePayload = {
  variantId: string | number;
  [key: string]: unknown;
};

export const trackRudderstackBlanksProductPriceChanged = async (
  rawPayload: BlanksProductPricePayload
) => {
  const { variantId, ...restPayload } = rawPayload;
  const variantInfo = await getVariantInfo(variantId);

  // eslint-disable-next-line camelcase
  const { is_app, store_code } = additionalPayload(rawPayload);

  const payload = {
    ...restPayload,
    // eslint-disable-next-line camelcase
    is_app,
    // eslint-disable-next-line camelcase
    store_code,
    variant_id: variantId,
    brand: variantInfo.product.brand,
    category: variantInfo.product.adwords_category, // Blanks or Transfers or Stickers
    currency: variantInfo.currency,
    favorited: variantInfo.favorite,
    url: window.location.href,
    image_url: variantInfo.product.image,
    position: variantInfo.position,
    name: variantInfo.product.name,
    size: variantInfo.size,
    sku: variantInfo.sku,
    variant: variantInfo.color
  };

  track("Product Price Edited", payload);
};

export const extractTextFromLink = (link: HTMLAnchorElement) => {
  let linkText = "";

  if (link.children.length > 0) {
    linkText = Array.from(link.children)
      .map($el => ($el as HTMLElement)?.textContent?.trim() || "")
      .join(" ")
      .trim();
  }

  if (linkText.length === 0) linkText = link.textContent?.trim() || "";

  return linkText;
};

export const trackRudderstackChatInteraction = (rawPayload: Record<string, unknown>) => {
  // eslint-disable-next-line camelcase
  const { is_app, store_code } = additionalPayload(rawPayload);

  const payload = {
    ...rawPayload,
    // eslint-disable-next-line camelcase
    store_code,
    // eslint-disable-next-line camelcase
    is_app,
    // eslint-disable-next-line max-lines
    location: getPageName() || window.location.pathname,
    platform: window.MOBILE_APP_PLATFORM || "web",
    url: window.location.href
  };

  track("Chat Interaction", payload);
};

export const trackRudderstackImagePlacementSelection = (
  action: string,
  trackProperties: Record<string, unknown>
) => {
  // eslint-disable-next-line camelcase
  const { catalog_number } = additionalPayload(trackProperties);

  // eslint-disable-next-line camelcase
  track(`Image Placement ${action}`, { ...trackProperties, catalog_number });
};

export const trackRudderstackImagePlacementQuantity = (
  trackProperties: Record<string, unknown>
) => {
  const catalogNumber = additionalPayload(trackProperties).catalog_number;

  track("Image Placement Quantity Adjusted", { ...trackProperties, catalog_number: catalogNumber });
};

export const trackRudderstackImagePlacementColor = (trackProperties: Record<string, unknown>) => {
  const catalogNumber = additionalPayload(trackProperties).catalog_number;

  track("Image Placement Color Selected", { ...trackProperties, catalog_number: catalogNumber });
};
